import { ConfirmModal } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";
import type { MenuItem } from "@/lib/api/types";

/**
 * "An ingredient in this dish is finished" — the till's half of B18 (unit 6).
 *
 * **It warns, it does not refuse**, which is the founder's answer and the
 * reason this is a confirm rather than a block: the primary action ADDS the
 * dish, and there is no path through this dialog that ends in a refusal. The
 * server is not told the waiter saw it, either — it records the state of the
 * kitchen onto the plate itself, so the evidence does not depend on the till
 * being honest about having asked.
 *
 * A dialog rather than only the tile's badge because the two answer different
 * moments: the badge is what a waiter reads while scanning the grid, and this
 * is what stops the tap they made anyway. The founder's answer is that a
 * waiter gets a CLEAR warning, and a 22px pill on a 172px tile mid-service is
 * not reliably clear.
 *
 * `tone="primary"`, deliberately not danger and deliberately not `caution`
 * either. Nothing here is destructive — adding the dish is the ordinary,
 * permitted outcome — and dressing it in red would say the till was refusing
 * something, which is the one reading the answer rules out. It is not the
 * caution rung because the act this dialog gates is adding a LINE: it reaches
 * nothing outside this screen until «إرسال للمطبخ», and the waiter removes the
 * line from the same panel. `confirmIcon={null}` used to sit here to keep the
 * trash glyph off the button; under `primary` it was already a no-op, and the
 * three call sites where it was load-bearing now declare `caution` instead.
 *
 * Shared by both add paths (the invoice grid and the table screen's first tap)
 * so the two cannot drift into warning about the same plate differently.
 */
export function IngredientWarningDialog({
  item,
  locale,
  onCancel,
  onConfirm,
}: {
  /** The dish being added, or null when nothing is pending. */
  item: MenuItem | null;
  locale: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const t = useTranslate();
  const names = (item?.ingredientsMarkedOut ?? [])
    // The list separator follows the reader, not the data: an Arabic comma
    // between Arabic ingredient names, a Latin one in English.
    .map((row) => row.name)
    .join(locale === "ar" ? "، " : ", ");

  return (
    <ConfirmModal
      isOpen={item !== null}
      onClose={onCancel}
      onConfirm={onConfirm}
      tone="primary"
      title={t((m) => m.pos.ingredientWarnTitle)}
      description={t((m) => m.pos.ingredientWarnBody, {
        item: item ? (locale === "en" && item.nameEn ? item.nameEn : item.nameAr) : "",
        items: names,
      })}
      confirmLabel={t((m) => m.pos.ingredientWarnConfirm)}
      cancelLabel={t((m) => m.common.cancel)}
      closeLabel={t((m) => m.common.cancel)}
    />
  );
}
